import React, { Fragment, useState } from 'react'
import { Plus as PlusIcon, Trash2 as Trash2Icon } from 'react-feather'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import { DriveTour, Select } from 'components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import * as service from 'service'
import useStyles from './styles'
import schema from './schema'
import theme from 'theme'

const ConfigForm = ({
  answerOptionTickets = [],
  users = [],
  setLoading,
  refresh,
  openTour,
  setOpenTour,
}) => {
  const [removeItems, setRemoveItems] = useState([])

  const {
    DATA_SUBJECT_CONFIG_STEPS_OBJECT,
    DATA_SUBJECT_CONFIG_DRIVER_STEP_2,
    DATA_SUBJECT_CONFIG_DRIVER_STEP_3,
    DATA_SUBJECT_CONFIG_DRIVER_STEP_4,
  } = constants.tours.tickets

  const snackbar = useSnackbar()
  const classes = useStyles()
  const history = useHistory()

  const defaultValues = {
    responsibles: answerOptionTickets
      .filter((option) => !isEmpty(option.responsibleUsers))
      .map((option) => ({
        answerOptionTicketId: option.id,
        userIds: option.responsibleUsers.map((u) => {
          const userObj = users.find((user) => user.id === u.id)
          return { id: u.id, name: userObj?.name || '' }
        }),
      })),
  }

  const { control, errors, handleSubmit } = useForm({
    validationSchema: schema,
    defaultValues,
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'responsibles',
  })

  const filteredSteps = DATA_SUBJECT_CONFIG_STEPS_OBJECT.filter(
    ({ element }) => {
      if (!isEmpty(fields)) {
        return element !== '#' + DATA_SUBJECT_CONFIG_DRIVER_STEP_2
      }

      return true
    },
  )

  const handleAppend = () => {
    append({
      answerOptionTicketId: '',
      userIds: [],
    })
  }

  const handleRemove = (item, index) => {
    const responsiblesRemove = {
      id: item?.answerOptionTicketId,
      userIds: [],
    }

    setRemoveItems((rest) => [...rest, responsiblesRemove])
    remove(index)
  }

  const handleReturn = () => history.goBack()

  const buildResponsibles = (data) =>
    data?.responsibles?.map((option) => ({
      id: option?.answerOptionTicketId,
      userIds: (option?.userIds ?? []).map((u) => u.id),
    })) ?? []

  const buildFilteredRemoveItems = (responsibles) =>
    removeItems.filter(
      (removed) => !responsibles.some((item) => item.id === removed.id),
    )

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const responsibles = buildResponsibles(data)
      const filteredRemoveItems = buildFilteredRemoveItems(responsibles)

      await service.dponet.titularTickets.changeResponsibles({
        responsibles: [...filteredRemoveItems, ...responsibles],
      })

      snackbar.open({
        message: 'Responsáveis atualizados com sucesso!',
        variant: 'success',
      })
      setRemoveItems([])
      refresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={theme.spacing(2)}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Typography variant="h5" color="primary">
        Definição de responsáveis
      </Typography>
      {fields.map((item, index) => {
        const selectedIds = fields
          .filter((_, i) => i !== index)
          .map((f) => f.answerOptionTicketId)

        return (
          <Fragment key={item.id}>
            <Paper
              className={classes.cardContainer}
              {...(index === 0 && { id: DATA_SUBJECT_CONFIG_DRIVER_STEP_2 })}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`responsibles[${index}].answerOptionTicketId`}
                    control={control}
                    as={
                      <Select
                        label="Resposta"
                        items={answerOptionTickets}
                        error={
                          !!errors?.responsibles?.[index]?.answerOptionTicketId
                        }
                        helperText={
                          <>
                            {
                              errors?.responsibles?.[index]
                                ?.answerOptionTicketId?.message
                            }
                          </>
                        }
                        fullWidth
                        fieldName="title"
                        disabledItems={selectedIds}
                      />
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  className={classes.responsibleContainer}
                >
                  <Controller
                    name={`responsibles[${index}].userIds`}
                    control={control}
                    onChange={([, value]) =>
                      value.map((user) => ({ id: user.id, name: user.name }))
                    }
                    as={
                      <Autocomplete
                        multiple
                        fullWidth
                        options={users}
                        getOptionLabel={(option) => option.name}
                        getOptionSelected={(option, value) =>
                          option.id === value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Responsáveis"
                            variant="outlined"
                            error={!!errors?.responsibles?.[index]?.userIds}
                            helperText={
                              <>
                                {
                                  errors?.responsibles?.[index]?.userIds
                                    ?.message
                                }
                              </>
                            }
                          />
                        )}
                      />
                    }
                  />
                  <Box>
                    <IconButton onClick={() => handleRemove(item, index)}>
                      <Trash2Icon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fragment>
        )
      })}
      <Paper id={DATA_SUBJECT_CONFIG_DRIVER_STEP_3}>
        <Button
          startIcon={<PlusIcon />}
          className={classes.plusButton}
          fullWidth
          onClick={handleAppend}
        >
          Adicionar novo responsável por resposta
        </Button>
      </Paper>
      <Box display="flex" justifyContent="flex-end" gridGap={theme.spacing(1)}>
        <Button variant="outlined" onClick={handleReturn}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          id={DATA_SUBJECT_CONFIG_DRIVER_STEP_4}
        >
          Salvar
        </Button>
      </Box>
      <DriveTour
        stepsMatrix={filteredSteps}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Box>
  )
}

ConfigForm.propTypes = {
  answerOptionTickets: PropTypes.arrayOf(PropTypes.object),
  users: PropTypes.arrayOf(PropTypes.object),
  setLoading: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  openTour: PropTypes.bool.isRequired,
  setOpenTour: PropTypes.func.isRequired,
}

export default ConfigForm
